import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ContractPdf from './ContractPdf';
import SignaturePad from './SignaturePad';
import { FIELD_KINDS, isInteractive, resolveMerge } from './contractFields';
import { isDeferredToken } from './contractBlocks';
import { getModel, money, hasPriceFields } from './billingModels';

// Public signing page. No authentication — the link IS the credential.
//
// Security posture (unchanged from the first version, and the reason this file
// is deliberately plain):
//   * the token goes out in the x-signing-token HEADER, never a query string,
//     so it stays out of server logs and Referer headers
//   * a no-referrer meta tag is installed on mount, and NO third-party asset
//     loads here at all — no fonts, no analytics, no error reporting. Anything
//     that phones home could carry the URL, and the URL is the credential
//   * nothing on this page is ever written to the console
//
// The experience is now field-driven: the actual document renders, the signer
// fills the boxes the sender placed on it, and signing is only possible once
// every required field is done. Consent is still captured as the exact string
// the server sent, because that is the evidence.

const FN_BASE = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`;
const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;

const C = {
  bg: '#0A1526', surface: '#16233F', border: '#28395C',
  text: '#e2e8f0', bright: '#f1f5f9', muted: '#7d8ea2',
  accent: '#00A0C8', danger: '#f87171', success: '#68d391', amber: '#f6c26b',
};

const prettyDate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.slice(0, 10).split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[Number(m) - 1]} ${Number(d)}, ${y}`;
};
const todayLabel = () => {
  const n = new Date();
  return prettyDate(`${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`);
};

export default function PublicSign({ token }) {
  const [state, setState] = useState('loading');   // loading|ready|signed|error
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [values, setValues] = useState({});        // fieldId -> {text|bool|dataUrl}
  const [padFor, setPadFor] = useState(null);      // field awaiting a signature
  const [agreed, setAgreed] = useState(false);
  // option_id -> true. A pick-one group keeps at most one.
  const [picked, setPicked] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [activeId, setActiveId] = useState(null);   // field being guided to
  const [zoom, setZoom] = useState(1);
  const shownAt = useRef(null);
  const fieldRefs = useRef({});
  const signBlockRef = useRef(null);

  // Stable identity. ContractPdf now holds its callbacks in refs, but passing a
  // fresh arrow on every render was what caused the document to remount on
  // every keystroke, so this side is kept stable too rather than relying on
  // the other side to keep forgiving it.
  const handlePdfError = useCallback((m) => setError(m), []);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => { try { document.head.removeChild(meta); } catch (_) {} };
  }, []);

  const call = useCallback(async (fn, body) => {
    const res = await fetch(`${FN_BASE}/${fn}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: ANON,
        Authorization: `Bearer ${ANON}`,
        'x-signing-token': token,
      },
      referrerPolicy: 'no-referrer',
      body: JSON.stringify(body || {}),
    });
    return res.json();
  }, [token]);

  const load = useCallback(async () => {
    try {
      const body = await call('get-public-contract');
      if (!body.ok) { setError(body.error || 'This link could not be opened.'); setState('error'); return; }
      setData(body);
      shownAt.current = new Date().toISOString();
      if (body.signer?.signed_at) { setState('signed'); return; }
      setState('ready');
    } catch (_) {
      setError('Could not reach the server. Check your connection and try again.');
      setState('error');
    }
  }, [call]);

  useEffect(() => { load(); }, [load]);

  // Only this signer's fields are theirs to fill. Everything else on the page
  // is shown for context but is not interactive.
  const myFields = useMemo(() => {
    if (!data?.fields) return [];
    return data.fields
      .filter((f) => isInteractive(f.field_kind) && f.signer_id === data.signer?.id)
      .sort((a, b) => (a.page_number - b.page_number) || (a.y_pct - b.y_pct) || (a.x_pct - b.x_pct));
  }, [data]);

  const isFilled = useCallback((f) => {
    const v = values[f.id];
    if (f.field_kind === 'checkbox') return v?.bool === true;
    if (f.field_kind === 'signature' || f.field_kind === 'initials') return !!v?.dataUrl;
    return !!(v?.text || '').trim();
  }, [values]);

  const outstanding = myFields.filter((f) => f.is_required && !isFilled(f));

  // ── Pricing options ─────────────────────────────────────────────────
  // A group settled by an earlier signer is shown as decided, not offered
  // again: the choice belongs to the agreement, not to each person signing it.
  const optionGroups = data?.option_groups || [];
  const settledGroups = new Set((data?.option_selections || []).map((s) => s.group_id));
  // Which OPTIONS were settled, not just which groups. A pick-any group can
  // hold several, so asking "did they choose this one" has to be a membership
  // test -- the cards used to find() the first settled option and compare ids,
  // which showed a second signer only one of the add-ons an earlier signer had
  // chosen and dimmed the rest as though they had been turned down. Derived in
  // ONE place because the cards and the "You are agreeing to" summary both need
  // it, and when each worked it out for itself they disagreed on the same page.
  const settledOptionIds = new Set((data?.option_selections || []).map((s) => s.option_id));
  const chosenIds = Object.keys(picked).filter((id) => picked[id]);
  const pickedInGroup = (g) => (g.options || []).filter((o) => picked[o.id]);
  const unanswered = optionGroups.filter(
    (g) => g.is_required && !settledGroups.has(g.id) && pickedInGroup(g).length === 0,
  );
  const chooseOption = (g, o) => setPicked((p) => {
    if (g.selection === 'multi') return { ...p, [o.id]: !p[o.id] };
    const next = { ...p };
    for (const sib of g.options || []) delete next[sib.id];
    next[o.id] = true;
    return next;
  });
  /**
   * THE PRICE, FROM THE FIELDS -- the same sentence the document prints.
   *
   * This used to be a third hand-written copy of the pricing wording, phrased
   * differently again ("$2,500 per month" against the document's "$2,500/mo"),
   * and the sender's free text REPLACED it when they had written any. So a
   * signer could be shown a description and no figure at all, and two people
   * reading the same agreement on the page and in the PDF saw it worded
   * differently.
   *
   * It now comes from src/billingModels.js, the registry the whole app prices
   * from — which has no imports of its own, so nothing follows it onto a page
   * a stranger loads — and is mirrored for the PDF by
   * supabase/functions/_shared/optionTerms.ts.
   */
  const optionPrice = (o) => {
    const m = getModel(o.billing_model_id);
    // An option whose model has no figures in it shows no price, exactly as
    // the document prints none -- "$0/hr" on a signing page would be a term.
    if (!m || !hasPriceFields(o.billing_model_id, o.billing_config)) return '';
    return m.summary(o.billing_config || {});
  };
  /** The sender's optional clarification, under the price rather than instead
   *  of it. */
  const optionNote = (o) => (o.body_text || '').trim();
  const activeField = myFields.find((f) => f.id === activeId) || null;
  const canSign = outstanding.length === 0 && unanswered.length === 0 && agreed
    && !!data?.contract && !data.contract.deadline_passed;

  // ── Guided signing ──────────────────────────────────────────────────
  //
  // One field is "active" at a time: the page zooms in on it, scrolls it to the
  // middle, and puts the Next button right beside it. The alternative -- a
  // button parked in a bar at the top -- means filling a field, losing your
  // place, and hunting for the control every single time.
  // Zoom is scaled to the field, so a small initials box gets more magnification
  // than a full-width signature line. The band is deliberately narrow: matching
  // every field exactly would mean the page lurching between wildly different
  // sizes as you move down it, which is worse than being slightly off.
  const FOCUS_MIN = 1.25;
  const FOCUS_MAX = 1.9;
  const TARGET_FIELD_PX = 44;

  const focusField = useCallback((f) => {
    if (!f) return;
    setActiveId(f.id);
    setZoom((z) => {
      // Measure what the field actually is right now rather than deriving it
      // from page dimensions -- this works whatever the page size or container.
      const h = fieldRefs.current[f.id]?.getBoundingClientRect().height || 0;
      if (!h) return Math.max(z, FOCUS_MIN);
      const want = z * (TARGET_FIELD_PX / h);
      return Math.min(FOCUS_MAX, Math.max(FOCUS_MIN, Number(want.toFixed(2))));
    });
  }, []);

  // Scroll after the page has repainted at the new zoom -- measuring before
  // that puts the field in the wrong place.
  useEffect(() => {
    if (!activeId) return undefined;
    const t = setTimeout(() => {
      const el = fieldRefs.current[activeId];
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      if (typeof el.focus === 'function') el.focus({ preventScroll: true });
    }, 280);
    return () => clearTimeout(t);
    // Deliberately NOT depending on zoom. focusField sets both in one handler,
    // so by the time this runs the new zoom is already applied -- and listing
    // zoom would re-snap to the field every time someone zoomed out by hand to
    // look at the rest of the page, which is the page fighting them again.
  }, [activeId]);

  const nextUnfilled = (fromId) => {
    const i = myFields.findIndex((f) => f.id === fromId);
    return myFields.slice(i + 1).find((f) => !isFilled(f))
        || myFields.find((f) => !isFilled(f) && f.id !== fromId)
        || null;
  };

  const advance = () => {
    const next = nextUnfilled(activeId);
    if (next) { focusField(next); return; }
    // Nothing left on the document: back out to normal size and hand them to
    // the part where they actually sign.
    setActiveId(null);
    setZoom(1);
    setTimeout(() => signBlockRef.current
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 90);
  };

  const sign = async () => {
    setSubmitting(true);
    setError('');
    try {
      const body = await call('submit-signature', {
        // Kept for the record and for the confirmation line; the signature
        // image is what appears on the document.
        typed_name: (data.signer?.name || '').trim() || 'Signed electronically',
        consent_acknowledged: agreed,
        authority_attested: agreed,
        consent_text: data?.consent?.text,
        consent_ui_version: data?.consent?.version,
        consent_displayed_at: shownAt.current,
        privacy_policy_version: 'privacy-2026-09-02',
        option_ids: chosenIds,
        fields: myFields.map((f) => ({
          id: f.id,
          text: values[f.id]?.text ?? null,
          bool: values[f.id]?.bool ?? null,
          image_base64: values[f.id]?.dataUrl ?? null,
        })),
      });
      if (!body.ok) { setError(body.error || 'Could not record your signature.'); setSubmitting(false); return; }
      setData((d) => ({ ...d, signer: { ...d.signer, signed_at: body.signed_at, typed_name: body.typed_name } }));
      setState('signed');
      // Refetch, because the executed copy did not exist when this page loaded.
      // document_url still points at the pre-signature original, so without this
      // "View the document" hands someone who just filled in a form the blank
      // version of it back -- which is exactly what it did.
      load();
    } catch (_) {
      setError('Could not reach the server. Your signature was not recorded.');
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children, wide) => (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text,
                  fontFamily: 'Inter, system-ui, -apple-system, sans-serif', padding: '22px 14px 60px' }}>
      <div style={{ maxWidth: wide ? '1000px' : '760px', margin: '0 auto' }}>{children}</div>
    </div>
  );

  if (state === 'loading') {
    return shell(<div style={{ padding: '60px 0', textAlign: 'center', color: C.muted }}>Loading…</div>);
  }

  if (state === 'error') {
    return shell(
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px',
                    padding: '32px', textAlign: 'center' }}>
        <div style={{ fontSize: '17px', fontWeight: 700, color: C.bright, marginBottom: '10px' }}>
          This link can’t be opened
        </div>
        <div style={{ fontSize: '14px', color: C.muted, lineHeight: 1.6 }}>{error}</div>
      </div>,
    );
  }

  const c = data.contract;
  const org = data.org;
  const signer = data.signer;

  if (state === 'signed') {
    return shell(
      <>
        <OrgHeader org={org} />
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px',
                      padding: '32px', textAlign: 'center' }}>
          <div style={{ fontSize: '34px', marginBottom: '10px' }}>✓</div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: C.bright, marginBottom: '8px' }}>
            Signed. Thank you.
          </div>
          <div style={{ fontSize: '14px', color: C.text, lineHeight: 1.6 }}>
            You signed <strong>{c.title}</strong> ({c.contract_number}).
          </div>
          <div style={{ fontSize: '13px', color: C.muted, marginTop: '12px', lineHeight: 1.6 }}>
            {org?.name || 'The sender'} still needs to countersign. You’ll receive the fully
            executed copy by email once they do.
          </div>
          {data.document_url && (
            <a href={data.document_url} target="_blank" rel="noreferrer noopener"
               style={{ display: 'inline-block', marginTop: '18px', padding: '9px 18px',
                        borderRadius: '7px', border: `1px solid ${C.border}`, color: C.accent,
                        textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>
              View the document
            </a>
          )}
        </div>
      </>,
    );
  }

  // ── field overlay on the rendered document ───────────────────────────────
  const overlay = (pageNumber) => (
    <div style={{ position: 'absolute', inset: 0 }}>
      {(data.fields || [])
        .filter((f) => f.anchor_kind === 'page' && f.page_number === pageNumber)
        .map((f) => {
          const def = FIELD_KINDS[f.field_kind] || {};
          const mine = isInteractive(f.field_kind) && f.signer_id === signer?.id;
          const filled = isFilled(f);
          const v = values[f.id] || {};

          const box = {
            position: 'absolute',
            left: `${f.x_pct * 100}%`, top: `${f.y_pct * 100}%`,
            width: `${f.w_pct * 100}%`, height: `${f.h_pct * 100}%`,
            boxSizing: 'border-box', borderRadius: '3px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden',
          };

          // Someone else's field, or a value the system fills: shown so the
          // document reads correctly, never interactive.
          //
          // NOTHING IS SHOWN THAT HAS NOT HAPPENED. This page is the document
          // as the signer is asked to agree to it, so a value drawn here reads
          // as a term of it. Two things used to be filled in that were not
          // true yet, and Austin caught both on the first real signing:
          //
          //   * EVERY date_signed box printed today, including the lines of
          //     parties who had not signed -- so the page showed the agreement
          //     already dated by both sides. Only the viewer's own line is
          //     honest here (they are signing now, and that is the date that
          //     will be stamped); the page is never told whether anyone else
          //     has signed, so for anyone else it must show nothing at all.
          //   * {{effective_date}} on an effective-on-signature contract
          //     resolved to today, filling in the very gap the renderer
          //     deliberately leaves ruled and blank. The page contradicted the
          //     document it was displaying. isDeferredToken is the same rule
          //     the renderer uses, so the two now agree.
          if (!mine) {
            let shown = null;
            if (f.field_kind === 'merge') {
              shown = isDeferredToken(f.merge_token, { contract: c })
                ? null
                : resolveMerge(f.merge_token, {
                    contract: c, org, signer, counterpartyLabel: data.counterparty_label,
                  });
            } else if (f.field_kind === 'date_signed') {
              shown = f.signer_id && f.signer_id === signer?.id ? todayLabel() : null;
            }
            return (
              <div key={f.id} style={{ ...box, color: '#0d1b2a', fontSize: '12px',
                                       padding: '0 3px', justifyContent: 'flex-start' }}>
                {shown && <span style={{ whiteSpace: 'nowrap', overflow: 'hidden',
                                         textOverflow: 'ellipsis' }}>{shown}</span>}
              </div>
            );
          }

          const active = f.id === activeId;
          const border = active ? `2px solid ${C.accent}`
                       : filled ? `1px solid ${C.success}`
                       : `2px dashed ${def.accent || C.accent}`;
          const bg = filled ? 'rgba(104,211,145,0.10)' : `${def.accent || C.accent}1f`;
          // A ring rather than a thicker border, so the box does not change
          // size when it becomes active and nudge its own contents around.
          const ring = active ? { boxShadow: `0 0 0 3px rgba(0,160,200,0.45)` } : null;

          if (f.field_kind === 'signature' || f.field_kind === 'initials') {
            return (
              <div key={f.id} ref={(el) => { fieldRefs.current[f.id] = el; }}
                   onClick={() => { setActiveId(f.id); setPadFor(f); }}
                   style={{ ...box, border, background: bg, cursor: 'pointer', ...ring }}>
                {v.dataUrl
                  ? <img src={v.dataUrl} alt="signature"
                         style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <span style={{ fontSize: '10px', fontWeight: 700, color: '#0d1b2a',
                                   whiteSpace: 'nowrap' }}>
                      {def.icon} {f.field_kind === 'initials' ? 'Initial' : 'Sign'} here
                    </span>}
              </div>
            );
          }

          if (f.field_kind === 'checkbox') {
            return (
              <div key={f.id} ref={(el) => { fieldRefs.current[f.id] = el; }}
                   onClick={() => { setActiveId(f.id);
                                    setValues((s) => ({ ...s, [f.id]: { bool: !v.bool } })); }}
                   title={f.label || 'Checkbox'}
                   style={{ ...box, border, background: bg, cursor: 'pointer',
                            fontSize: '13px', color: '#0d1b2a', fontWeight: 700, ...ring }}>
                {v.bool ? '✓' : ''}
              </div>
            );
          }

          return (
            <input key={f.id} ref={(el) => { fieldRefs.current[f.id] = el; }}
                   value={v.text || ''} placeholder={f.label || 'Type here'}
                   onChange={(e) => setValues((s) => ({ ...s, [f.id]: { text: e.target.value } }))}
                   onFocus={() => setActiveId(f.id)}
                   style={{ ...box, border, background: filled ? 'rgba(104,211,145,0.10)' : '#fffbe8',
                            color: '#0d1b2a', fontSize: '12px', padding: '0 4px', outline: 'none',
                            ...ring }} />
          );
        })}

      {/* The Next control rides along with the active field instead of living
          in a bar at the top. Filling a field and then having to go hunting for
          the button is the thing that made this feel like a form rather than a
          document you are being walked through. */}
      {activeField && activeField.page_number === pageNumber && (() => {
        // Below the field by preference: a button that is always in the same
        // place relative to what you just filled is easier to build a habit
        // around than one that changes sides depending on the layout. It only
        // moves above when the field is close enough to the bottom that below
        // would fall off the page.
        // 0.95, not 0.9: a field ending at 91% of the page still has ~70pt of
        // paper beneath it, which is plenty for a button. Flipping too eagerly
        // put Next above the very first field and undercut the whole point of
        // it being consistently below.
        const nearBottom = (activeField.y_pct + activeField.h_pct) > 0.95;
        const more = !!nextUnfilled(activeField.id);
        return (
          <button key="guide-next" onClick={advance}
                  style={{ position: 'absolute', zIndex: 5, whiteSpace: 'nowrap',
                           left: `${activeField.x_pct * 100}%`,
                           ...(nearBottom
                             ? { bottom: `${(1 - activeField.y_pct) * 100}%`, marginBottom: '8px' }
                             : { top: `${(activeField.y_pct + activeField.h_pct) * 100}%`,
                                 marginTop: '8px' }),
                           padding: '7px 14px', borderRadius: '999px', border: 'none',
                           background: C.accent, color: '#04121d', fontSize: '12px',
                           fontWeight: 700, cursor: 'pointer',
                           boxShadow: '0 2px 10px rgba(0,0,0,0.35)' }}>
            {more ? 'Next →' : 'Review & sign →'}
          </button>
        );
      })()}
    </div>
  );

  const blocked = c.deadline_passed;

  return shell(
    <>
      <OrgHeader org={org} />

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px',
                    padding: '18px', marginBottom: '14px' }}>
        <div style={{ fontSize: '17px', fontWeight: 700, color: C.bright }}>{c.title}</div>
        <div style={{ fontSize: '12px', color: C.muted, marginTop: '3px' }}>
          {c.contract_number} · for {signer?.signing_on_behalf_of || data.counterparty_label || signer?.email}
        </div>
        <div style={{ fontSize: '13px', color: C.text, marginTop: '10px', lineHeight: 1.7 }}>
          <div>Effective {c.effective_on_signature && !c.effective_date
            ? 'on the date it’s signed' : (prettyDate(c.effective_date) || 'not specified')}</div>
          <div>{c.is_evergreen ? 'No end date — runs until either party ends it'
            : prettyDate(c.end_date) ? `Ends ${prettyDate(c.end_date)}` : 'No end date specified'}</div>
        </div>
      </div>

      {blocked && (
        <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)',
                      borderRadius: '10px', padding: '14px', marginBottom: '14px',
                      fontSize: '13px', color: C.danger, lineHeight: 1.6 }}>
          The signing window for this agreement has closed. Contact {org?.name || 'the sender'} for
          an updated copy.
        </div>
      )}

      {/* Progress rail — the "3 fields remaining" affordance every e-sign tool has,
          because on a long document the boxes are easy to miss. */}
      {!blocked && myFields.length > 0 && (
        <div style={{ position: 'sticky', top: 0, zIndex: 40, background: C.surface,
                      border: `1px solid ${C.border}`, borderRadius: '10px', padding: '11px 14px',
                      marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '12px',
                      flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: outstanding.length ? C.amber : C.success,
                         fontWeight: 600 }}>
            {outstanding.length
              ? `${outstanding.length} field${outstanding.length === 1 ? '' : 's'} left to fill`
              : 'All fields complete'}
          </span>
          {outstanding.length > 0 && (
            <button onClick={() => focusField(outstanding[0])}
                    style={{ padding: '6px 13px', borderRadius: '7px', border: `1px solid ${C.accent}`,
                             background: 'transparent', color: C.accent, fontSize: '12px',
                             fontWeight: 600, cursor: 'pointer' }}>
              {activeId ? 'Back to next field' : 'Start filling'}
            </button>
          )}
          <span style={{ fontSize: '11px', color: C.muted, marginLeft: 'auto' }}>
            {myFields.filter(isFilled).length} of {myFields.length} done
          </span>
        </div>
      )}

      <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '12px', padding: '14px',
                    marginBottom: '14px' }}>
        <ContractPdf url={data.document_url} C={C} renderOverlay={overlay} maxWidth={900}
                     onError={handlePdfError} zoom={zoom} onZoomChange={setZoom}
                     fullFooter={({ exitFull }) => (
                       <div style={{ display: 'flex', alignItems: 'center', gap: '12px',
                                     flexWrap: 'wrap', justifyContent: 'center' }}>
                         <span style={{ fontSize: '12px',
                                        color: outstanding.length ? C.amber : C.success }}>
                           {outstanding.length
                             ? `${outstanding.length} field${outstanding.length === 1 ? '' : 's'} left to fill`
                             : 'All fields complete'}
                         </span>
                         <button onClick={() => {
                                   exitFull();
                                   // After the overlay closes, put them where the
                                   // decision is rather than back at the top.
                                   setTimeout(() => signBlockRef.current
                                     ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
                                 }}
                                 style={{ padding: '9px 20px', borderRadius: '8px', border: 'none',
                                          fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                                          background: outstanding.length ? C.border : C.accent,
                                          color: outstanding.length ? C.text : '#04121d' }}>
                           {outstanding.length ? 'Continue anyway' : 'Continue to sign'}
                         </button>
                       </div>
                     )} />
      </div>

      {/* ── Choose ─────────────────────────────────────────────────── */}
      {!blocked && optionGroups.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px',
                      padding: '20px', marginBottom: '16px' }}>
          {optionGroups.map((g) => {
            const settled = settledGroups.has(g.id);
            return (
              <div key={g.id} style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: C.bright }}>{g.label}</div>
                <div style={{ fontSize: '12px', color: C.muted, marginTop: '3px', lineHeight: 1.6 }}>
                  {settled
                    ? 'Already chosen for this agreement.'
                    : g.help_text || (g.selection === 'multi' ? 'Choose any that apply.' : 'Choose one.')}
                </div>

                <div style={{ display: 'grid', gap: '10px', marginTop: '12px' }}>
                  {(g.options || []).map((o) => {
                    const on = settled ? settledOptionIds.has(o.id) : !!picked[o.id];
                    const price = optionPrice(o);
                    const note = optionNote(o);
                    return (
                      <button key={o.id} type="button" disabled={settled}
                              onClick={() => chooseOption(g, o)}
                              style={{ textAlign: 'left', padding: '14px 16px', borderRadius: '10px',
                                       cursor: settled ? 'default' : 'pointer',
                                       border: `2px solid ${on ? C.accent : C.border}`,
                                       background: on ? `${C.accent}14` : 'transparent',
                                       opacity: settled && !on ? 0.45 : 1 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '14px', fontWeight: 700,
                                         color: on ? C.accent : C.bright }}>{o.label}</span>
                          {o.is_recommended && !settled && (
                            <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em',
                                           textTransform: 'uppercase', color: C.accent,
                                           border: `1px solid ${C.accent}`, borderRadius: '20px',
                                           padding: '1px 7px' }}>Recommended</span>
                          )}
                          {settled && on && (
                            <span style={{ fontSize: '11px', color: C.muted }}>· chosen</span>
                          )}
                        </div>
                        {/* The price first and in the card's own voice: this
                            is the figure being chosen between. */}
                        {price && (
                          <div style={{ fontSize: '15px', fontWeight: 700, marginTop: '6px',
                                        color: on ? C.accent : C.bright }}>
                            {price}
                          </div>
                        )}
                        {(o.charges || []).length > 0 && (
                          <div style={{ fontSize: '12px', color: C.muted, marginTop: '4px' }}>
                            {(o.charges || []).map((c, i) => (
                              <div key={i}>
                                {c.label || 'One-off charge'}: {money(c.amount)} once
                              </div>
                            ))}
                          </div>
                        )}
                        {note && (
                          <div style={{ fontSize: '12px', color: C.muted, marginTop: '6px', lineHeight: 1.6 }}>
                            {note}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!blocked && (
        <div ref={signBlockRef}
             style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px',
                      padding: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: C.bright, marginBottom: '4px' }}>
            Sign this agreement
          </div>
          <div style={{ fontSize: '12px', color: C.muted, marginBottom: '14px' }}>
            Signing as {signer?.name ? `${signer.name} · ` : ''}{signer?.email}
          </div>

          {/* THE CONFIRMATION STEP. Cards are easy to click past, and what is
              being agreed to here is a price. Immediately above the consent
              box, in plain words, is what this signature commits to -- so
              nobody discovers it on their first invoice. */}
          {(chosenIds.length > 0 || settledGroups.size > 0) && (
            <div style={{ border: `1px solid ${C.accent}`, borderRadius: '10px',
                          padding: '12px 14px', marginBottom: '14px',
                          background: `${C.accent}0D` }}>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em',
                            textTransform: 'uppercase', color: C.accent, marginBottom: '6px' }}>
                You are agreeing to
              </div>
              {optionGroups.map((g) => {
                const settled = settledGroups.has(g.id);
                const chosen = settled
                  ? (g.options || []).filter((o) => settledOptionIds.has(o.id))
                  : pickedInGroup(g);
                if (!chosen.length) return null;
                return (
                  <div key={g.id} style={{ fontSize: '13px', color: C.text, lineHeight: 1.7 }}>
                    {chosen.map((o) => (
                      <div key={o.id}>
                        <strong style={{ color: C.bright }}>{o.label}</strong>
                        {optionPrice(o) ? ` — ${optionPrice(o)}` : ''}
                        {settled && <span style={{ color: C.muted }}> (chosen earlier)</span>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {unanswered.length > 0 && (
            <div style={{ fontSize: '12px', color: C.muted, marginBottom: '14px', lineHeight: 1.6 }}>
              Choose an option under {unanswered.map((g) => `“${g.label}”`).join(' and ')} before signing.
            </div>
          )}

          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '14px',
                          cursor: 'pointer' }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                   style={{ marginTop: '3px', width: '17px', height: '17px', flexShrink: 0 }} />
            <span style={{ fontSize: '12.5px', color: C.text, lineHeight: 1.65 }}>
              {data.consent.text}
            </span>
          </label>

          {outstanding.length > 0 && (
            <div style={{ fontSize: '12px', color: C.amber, marginBottom: '10px' }}>
              Fill the remaining {outstanding.length} field{outstanding.length === 1 ? '' : 's'} on the
              document above before signing.
            </div>
          )}

          {error && (
            <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '8px',
                          background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)',
                          fontSize: '13px', color: C.danger }}>{error}</div>
          )}

          <button onClick={sign} disabled={!canSign || submitting}
                  style={{ width: '100%', padding: '13px', borderRadius: '8px', border: 'none',
                           fontSize: '15px', fontWeight: 700,
                           cursor: canSign ? 'pointer' : 'default',
                           background: canSign ? C.accent : C.border,
                           color: canSign ? '#04121d' : C.muted }}>
            {submitting ? 'Recording your signature…' : 'Finish signing'}
          </button>

          <div style={{ fontSize: '11px', color: C.muted, marginTop: '12px', lineHeight: 1.6 }}>
            Your signature, the date and time, and your IP address are recorded as evidence.
            {org?.name || 'The sender'} countersigns after you, and you’ll receive the fully
            executed copy by email.
          </div>
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: '22px', fontSize: '11px', color: C.muted }}>
        Sent securely via TimeKeep · Do not forward this link — it lets anyone sign as you
      </div>

      {padFor && (
        <SignaturePad
          mode={padFor.field_kind === 'initials' ? 'initials' : 'signature'}
          defaultName={signer?.name || ''}
          C={C}
          onCancel={() => setPadFor(null)}
          onDone={({ dataUrl }) => {
            setValues((s) => ({ ...s, [padFor.id]: { dataUrl } }));
            setPadFor(null);
          }} />
      )}
    </>,
    true,
  );
}

function OrgHeader({ org }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: '18px' }}>
      {org?.logo_url
        ? <img src={org.logo_url} alt={org.name || ''} style={{ maxHeight: '44px', maxWidth: '190px' }} />
        : <div style={{ fontSize: '17px', fontWeight: 700, color: C.bright }}>{org?.name || 'TimeKeep'}</div>}
    </div>
  );
}
